import { useState, useMemo } from "react";
import { useActiveStore } from "@/hooks/use-active-store";
import { useRevenueAnalytics, useCustomerAnalytics, useGeoAnalytics, useProductAnalytics } from "@/hooks/use-analytics";
import { DatePicker } from "@/components/ui/date-picker";
import { Skeleton } from "@/components/ui/skeleton";
import { cn, formatCurrency, calcDelta } from "@/lib/utils";
import { DeltaBadge } from "@/components/ui/delta-badge";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { AbcTab } from "@/components/analytics/abc-tab";

type Tab = "revenue" | "customers" | "geo" | "products" | "abc";
type Period = "7d" | "30d" | "90d" | "year" | "custom";

const TABS: { value: Tab; label: string }[] = [
  { value: "revenue", label: "Prihodi" },
  { value: "customers", label: "Kupci" },
  { value: "geo", label: "Geo" },
  { value: "products", label: "Proizvodi" },
  { value: "abc", label: "ABC Analiza" },
];

const PERIODS: { value: Period; label: string }[] = [
  { value: "7d", label: "7 dana" },
  { value: "30d", label: "30 dana" },
  { value: "90d", label: "90 dana" },
  { value: "year", label: "Godina" },
  { value: "custom", label: "Prilagođeno" },
];

const COLORS = ["#333333", "#666666", "#999999", "#bbbbbb", "#dddddd", "#444444", "#777777", "#aaaaaa", "#cccccc", "#eeeeee"];

function getDateRange(period: Period): { after: string; before: string } {
  const now = new Date();
  const before = now.toISOString();
  let after: Date;
  switch (period) {
    case "7d": after = new Date(now.getTime() - 7 * 86400000); break;
    case "30d": after = new Date(now.getTime() - 30 * 86400000); break;
    case "90d": after = new Date(now.getTime() - 90 * 86400000); break;
    case "year": after = new Date(now.getFullYear(), 0, 1); break;
    default: after = new Date(now.getTime() - 30 * 86400000); break;
  }
  return { after: after.toISOString(), before };
}

function fmtKM(value: number): string {
  return formatCurrency(value, "KM");
}

function DonutChart({ data, dataKey, nameKey }: { data: any[]; dataKey: string; nameKey: string }) {
  if (!data || data.length === 0) return <p className="text-xs text-muted-foreground">Nema podataka</p>;
  const total = data.reduce((sum, d) => sum + (d[dataKey] || 0), 0);
  return (
    <div className="flex items-center gap-4">
      <div className="w-28 h-28 shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} dataKey={dataKey} nameKey={nameKey} cx="50%" cy="50%" innerRadius={30} outerRadius={50} strokeWidth={1}>
              {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Pie>
            <Tooltip formatter={(value) => fmtKM(Number(value))} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="flex flex-col gap-1 min-w-0 flex-1">
        {data.slice(0, 8).map((item, i) => (
          <div key={i} className="flex items-center gap-2 text-xs">
            <div className="w-2.5 h-2.5 shrink-0 rounded-sm" style={{ background: COLORS[i % COLORS.length] }} />
            <span className="truncate flex-1">{item[nameKey]}</span>
            <span className="tabular-nums font-medium shrink-0">{fmtKM(item[dataKey])}</span>
            <span className="tabular-nums text-muted-foreground shrink-0 w-8 text-right">
              {total > 0 ? Math.round((item[dataKey] / total) * 100) : 0}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function RevenueTab({ storeId, after, before }: { storeId: string; after: string; before: string }) {
  const { data, isLoading } = useRevenueAnalytics(storeId, after, before, true);

  if (isLoading) return <div className="flex flex-col gap-4"><Skeleton className="h-20 w-full" /><Skeleton className="h-48 w-full" /></div>;
  if (!data) return null;

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-3 gap-3">
        <div className="ring-1 ring-foreground/10 p-3 flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Prihod</span>
          <div className="flex items-baseline gap-1.5">
            <span className="text-sm font-medium tabular-nums">{fmtKM(data.totals.net_revenue)}</span>
            <DeltaBadge delta={calcDelta(data.totals.net_revenue, data.prev_totals.net_revenue)} />
          </div>
        </div>
        <div className="ring-1 ring-foreground/10 p-3 flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Narudžbe</span>
          <div className="flex items-baseline gap-1.5">
            <span className="text-sm font-medium tabular-nums">{data.totals.orders_count}</span>
            <DeltaBadge delta={calcDelta(data.totals.orders_count, data.prev_totals.orders_count)} />
          </div>
        </div>
        <div className="ring-1 ring-foreground/10 p-3 flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Povrati</span>
          <span className="text-sm font-medium tabular-nums">{fmtKM(Math.abs(data.totals.refunds))}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div className="ring-1 ring-foreground/10 p-4">
          <h3 className="text-xs font-medium mb-3">Po plaćanju</h3>
          <DonutChart data={data.by_payment} dataKey="revenue" nameKey="title" />
        </div>
        <div className="ring-1 ring-foreground/10 p-4">
          <h3 className="text-xs font-medium mb-3">Po dostavi</h3>
          <DonutChart data={data.by_shipping} dataKey="revenue" nameKey="title" />
        </div>
        <div className="ring-1 ring-foreground/10 p-4">
          <h3 className="text-xs font-medium mb-3">Po kategoriji</h3>
          <DonutChart data={data.by_category} dataKey="revenue" nameKey="name" />
        </div>
        <div className="ring-1 ring-foreground/10 p-4">
          <h3 className="text-xs font-medium mb-3">Po kuponu</h3>
          <DonutChart data={data.by_coupon} dataKey="discount_total" nameKey="code" />
        </div>
      </div>
    </div>
  );
}

function CustomersTab({ storeId, after, before }: { storeId: string; after: string; before: string }) {
  const { data, isLoading } = useCustomerAnalytics(storeId, after, before, true);

  if (isLoading) return <div className="flex flex-col gap-4"><Skeleton className="h-20 w-full" /><Skeleton className="h-48 w-full" /></div>;
  if (!data) return null;

  const s = data.summary;
  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <div className="ring-1 ring-foreground/10 p-3 flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Kupci</span>
          <div className="flex items-baseline gap-1.5">
            <span className="text-sm font-medium tabular-nums">{s.total_customers}</span>
            <DeltaBadge delta={calcDelta(s.total_customers, data.prev_summary.total_customers)} />
          </div>
        </div>
        <div className="ring-1 ring-foreground/10 p-3 flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Novi</span>
          <span className="text-sm font-medium tabular-nums">{s.new_customers}</span>
        </div>
        <div className="ring-1 ring-foreground/10 p-3 flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Vraćaju se</span>
          <span className="text-sm font-medium tabular-nums">{s.returning_customers}</span>
        </div>
        <div className="ring-1 ring-foreground/10 p-3 flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Stopa povrata</span>
          <span className="text-sm font-medium tabular-nums">{(s.repeat_rate * 100).toFixed(0)}%</span>
        </div>
        <div className="ring-1 ring-foreground/10 p-3 flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Prosj. vrijednost</span>
          <span className="text-sm font-medium tabular-nums">{fmtKM(s.avg_lifetime_value)}</span>
        </div>
        <div className="ring-1 ring-foreground/10 p-3 flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Prosj. dana između</span>
          <span className="text-sm font-medium tabular-nums">{s.avg_days_between_orders}d</span>
        </div>
      </div>

      <div className="ring-1 ring-foreground/10">
        <div className="px-3 py-2.5 border-b">
          <span className="text-xs font-medium">Najbolji kupci</span>
        </div>
        <div className="divide-y">
          {data.top_customers.map((c) => (
            <div key={c.email} className="flex items-center gap-3 px-3 py-2 text-xs">
              <span className="font-medium flex-1 min-w-0 truncate">{c.name}</span>
              <span className="text-muted-foreground truncate max-w-[140px]">{c.email}</span>
              <span className="tabular-nums text-muted-foreground shrink-0">{c.orders_count} nar.</span>
              <span className="tabular-nums font-medium shrink-0">{fmtKM(c.total_spent)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function GeoTab({ storeId, after, before }: { storeId: string; after: string; before: string }) {
  const { data, isLoading } = useGeoAnalytics(storeId, after, before, true);

  if (isLoading) return <Skeleton className="h-64 w-full" />;
  if (!data) return null;

  const top10 = data.by_city.slice(0, 10);
  return (
    <div className="flex flex-col gap-6">
      <div className="ring-1 ring-foreground/10 p-4">
        <h3 className="text-xs font-medium mb-3">Prihod po gradu</h3>
        <DonutChart data={top10} dataKey="revenue" nameKey="city" />
      </div>

      <div className="ring-1 ring-foreground/10">
        <div className="px-3 py-2.5 border-b">
          <span className="text-xs font-medium">Svi gradovi</span>
        </div>
        <div className="divide-y max-h-80 overflow-y-auto">
          {data.by_city.map((city, i) => (
            <div key={city.city} className="flex items-center gap-3 px-3 py-2 text-xs">
              <span className="tabular-nums text-muted-foreground w-5 text-right shrink-0">{i + 1}.</span>
              <span className="font-medium flex-1 min-w-0 truncate">{city.city}</span>
              <span className="tabular-nums text-muted-foreground shrink-0">{city.orders} nar.</span>
              <span className="tabular-nums font-medium shrink-0">{fmtKM(city.revenue)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ProductsTab({ storeId, after, before }: { storeId: string; after: string; before: string }) {
  const { data, isLoading } = useProductAnalytics(storeId, after, before, true);

  if (isLoading) return <div className="flex flex-col gap-4"><Skeleton className="h-48 w-full" /><Skeleton className="h-48 w-full" /></div>;
  if (!data) return null;

  return (
    <div className="flex flex-col gap-6">
      <div className="ring-1 ring-foreground/10 p-4">
        <h3 className="text-xs font-medium mb-3">Najprodavaniji</h3>
        <DonutChart data={data.top_sellers} dataKey="revenue" nameKey="name" />
      </div>

      {data.stock_velocity.length > 0 && (
        <div className="ring-1 ring-foreground/10">
          <div className="px-3 py-2.5 border-b">
            <span className="text-xs font-medium">Brzina prodaje</span>
          </div>
          <div className="divide-y">
            {data.stock_velocity.map((p) => (
              <div key={p.id} className="flex items-center gap-3 px-3 py-2 text-xs">
                <span className="font-medium flex-1 min-w-0 truncate">{p.name}</span>
                <span className="tabular-nums text-muted-foreground shrink-0">{p.avg_sold_per_week}/sed</span>
                <span className="tabular-nums text-muted-foreground shrink-0">zaliha: {p.stock}</span>
                <span className={cn("tabular-nums font-medium shrink-0", p.weeks_until_out < 3 ? "text-red-500" : "")}>
                  {p.weeks_until_out < 999 ? `${p.weeks_until_out} sed` : "∞"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.slow_movers.length > 0 && (
        <div className="ring-1 ring-foreground/10">
          <div className="px-3 py-2.5 border-b">
            <span className="text-xs font-medium">Spori artikli (30+ dana bez prodaje)</span>
          </div>
          <div className="divide-y">
            {data.slow_movers.map((p) => (
              <div key={p.id} className="flex items-center gap-3 px-3 py-2 text-xs">
                <span className="font-medium flex-1 min-w-0 truncate">{p.name}</span>
                <span className="tabular-nums text-muted-foreground shrink-0">zaliha: {p.stock}</span>
                <span className="tabular-nums text-red-500 shrink-0">{p.days_since_last_sale}d</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.frequently_together.length > 0 && (
        <div className="ring-1 ring-foreground/10">
          <div className="px-3 py-2.5 border-b">
            <span className="text-xs font-medium">Često kupljeno zajedno</span>
          </div>
          <div className="divide-y">
            {data.frequently_together.map((pair, i) => (
              <div key={i} className="flex items-center gap-3 px-3 py-2 text-xs">
                <span className="flex-1 min-w-0 truncate">{pair.product_a_name} + {pair.product_b_name}</span>
                <span className="tabular-nums font-medium shrink-0">{pair.times_together}x</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function AnalyticsPage() {
  const { storeId } = useActiveStore();
  const [tab, setTab] = useState<Tab>("revenue");
  const [period, setPeriod] = useState<Period>("30d");
  const [customAfter, setCustomAfter] = useState("");
  const [customBefore, setCustomBefore] = useState("");

  const { after, before } = useMemo(() => {
    if (period === "custom" && customAfter && customBefore) {
      return { after: new Date(customAfter).toISOString(), before: new Date(customBefore + "T23:59:59").toISOString() };
    }
    return getDateRange(period);
  }, [period, customAfter, customBefore]);

  if (!storeId) {
    return <div className="flex items-center justify-center h-full"><p className="text-muted-foreground">Odaberite trgovinu</p></div>;
  }

  return (
    <div className="p-4 flex flex-col gap-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-sm font-medium">Analitika</h2>
        <div className="flex items-center gap-2">
          <div className="flex gap-0">
            {PERIODS.map((p) => (
              <button
                key={p.value}
                type="button"
                onClick={() => setPeriod(p.value)}
                className={cn(
                  "px-2.5 py-1 text-xs font-medium transition-colors",
                  period === p.value ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"
                )}
              >
                {p.label}
              </button>
            ))}
          </div>
          {period === "custom" && (
            <div className="flex items-center gap-1">
              <DatePicker value={customAfter} onChange={setCustomAfter} placeholder="Od" className="h-7 text-xs w-28" />
              <DatePicker value={customBefore} onChange={setCustomBefore} placeholder="Do" className="h-7 text-xs w-28" />
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-0 border-b border-foreground/10">
        {TABS.map((t) => (
          <button
            key={t.value}
            type="button"
            onClick={() => setTab(t.value)}
            className={cn(
              "shrink-0 px-3 pb-2 pt-0.5 text-xs font-medium transition-colors relative",
              "hover:text-foreground",
              tab === t.value ? "text-foreground" : "text-muted-foreground"
            )}
          >
            {t.label}
            {tab === t.value && <span className="absolute bottom-0 left-3 right-3 h-px bg-foreground" />}
          </button>
        ))}
      </div>

      {tab === "revenue" && <RevenueTab storeId={storeId} after={after} before={before} />}
      {tab === "customers" && <CustomersTab storeId={storeId} after={after} before={before} />}
      {tab === "geo" && <GeoTab storeId={storeId} after={after} before={before} />}
      {tab === "products" && <ProductsTab storeId={storeId} after={after} before={before} />}
      {tab === "abc" && <AbcTab storeId={storeId} after={after} before={before} />}
    </div>
  );
}
